import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const ToastContext = createContext(null);

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback((message, type = 'info', duration = 4000) => {
    const id = Date.now() + Math.random();
    const toast = { id, message, type };
    
    setToasts(prev => [...prev, toast]);
    
    if (duration > 0) {
      setTimeout(() => {
        removeToast(id);
      }, duration);
    }
    
    return id;
  }, []);

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  const success = useCallback((message, duration) => addToast(message, 'success', duration), [addToast]);
  const error = useCallback((message, duration) => addToast(message, 'error', duration), [addToast]);
  const info = useCallback((message, duration) => addToast(message, 'info', duration), [addToast]);
  const warning = useCallback((message, duration) => addToast(message, 'warning', duration), [addToast]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && toasts.length > 0) {
        removeToast(toasts[0].id);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [toasts, removeToast]);

  return (
    <ToastContext.Provider value={{ toasts, addToast, removeToast, success, error, info, warning }}>
      {children}
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </ToastContext.Provider>
  );
};

const ToastContainer = ({ toasts, removeToast }) => {
  return (
    <div className="fixed top-4 left-1/2 -translate-x-1/2 z-toast flex flex-col items-center gap-2 pointer-events-none w-full max-w-sm px-4" role="region" aria-label="Notifications">
      <AnimatePresence mode="sync">
        {toasts.map(toast => (
          <Toast key={toast.id} toast={toast} onClose={() => removeToast(toast.id)} />
        ))}
      </AnimatePresence>
    </div>
  );
};

const Toast = ({ toast, onClose }) => {
  const typeConfig = {
    success: { role: 'status', icon: 'check_circle', label: 'Success', colors: 'bg-emerald-50 border-emerald-200 text-emerald-800' },
    error: { role: 'alert', icon: 'error', label: 'Error', colors: 'bg-red-50 border-red-200 text-red-800' },
    info: { role: 'status', icon: 'info', label: 'Information', colors: 'bg-blue-50 border-blue-200 text-blue-800' },
    warning: { role: 'alert', icon: 'warning', label: 'Warning', colors: 'bg-amber-50 border-amber-200 text-amber-800' },
  };

  const config = typeConfig[toast.type] || typeConfig.info;

  return (
    <motion.div
      initial={{ opacity: 0, y: -20, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -10, scale: 0.95 }}
      transition={{ type: 'spring', stiffness: 300, damping: 25 }}
      className={`pointer-events-auto w-full flex items-center gap-3 px-4 py-3 rounded-xl border shadow-lg backdrop-blur-sm ${config.colors}`}
      role={config.role}
      aria-label={`${config.label}: ${toast.message}`}
    >
      <span className="material-symbols-outlined text-xl flex-shrink-0" style={{ fontVariationSettings: "'FILL' 1" }}>
        {config.icon}
      </span>
      <span className="text-sm font-medium flex-1">{toast.message}</span>
      <button 
        onClick={onClose}
        className="flex-shrink-0 p-1 rounded-full hover:bg-black/5 transition-colors"
        aria-label={`Close ${config.label} notification`}
      >
        <span className="material-symbols-outlined text-lg">close</span>
      </button>
    </motion.div>
  );
};

export default ToastProvider;
